import { inject, Injectable } from '@angular/core';
import { FormControl, ValidatorFn, Validators } from '@angular/forms';
import { I18N_OPTIONS_TOKEN, POSTAL_CODE_VALIDATOR_TOKEN } from '../injection-tokens';

export type FieldFormat = 'none' | 'mobile' | 'email' | 'postcode';

/** An unrecognised rule resolves to no check. */
export function fieldFormatForRule(rule: string | null | undefined): FieldFormat {
    switch (rule) {
        case 'Mobile':
            return 'mobile';
        case 'Email':
            return 'email';
        case 'Postcode':
            return 'postcode';
        default:
            return 'none';
    }
}

@Injectable({ providedIn: 'root' })
export class FieldFormatValidator {
    private readonly i18nOptions = inject(I18N_OPTIONS_TOKEN);
    private readonly postalCodeValidators = inject(POSTAL_CODE_VALIDATOR_TOKEN);

    /** Composed once: callers revalidate on every keystroke, and composing recompiles a locale regex. */
    private readonly composedByFormat: Partial<Record<FieldFormat, ValidatorFn | null>> = {
        mobile: Validators.compose([Validators.pattern(this.i18nOptions.phoneNumber.regularExpressionMask)]),
        email: Validators.compose([Validators.email]),
        postcode: Validators.compose(this.postalCodeValidators),
    };

    /** Formats whose value is typed with separators the locale mask does not allow. */
    private static readonly DIGITS_RETRY_FORMATS: readonly FieldFormat[] = ['mobile', 'postcode'];

    /**
     * A blank value passes: presence is the caller's concern.
     * Mobile and postcode retry on digits only, so "0412 345 678" passes a digit-exact locale mask.
     */
    isValid(format: FieldFormat, value: string): boolean {
        const trimmed = value.trim();
        const composed = this.composedByFormat[format];
        if (!composed || !trimmed) {
            return true;
        }

        if (composed(new FormControl(trimmed)) === null) {
            return true;
        }

        if (!FieldFormatValidator.DIGITS_RETRY_FORMATS.includes(format)) {
            return false;
        }

        const digitsOnly = trimmed.replace(/\D/g, '');
        if (digitsOnly === '') {
            return false;
        }

        if (digitsOnly !== trimmed && composed(new FormControl(digitsOnly)) === null) {
            return true;
        }

        return format === 'mobile' && this.i18nOptions.phoneNumber.digitsOnlyMask.test(digitsOnly);
    }
}
